import { first, last, sum } from "lodash";
import { Model } from "@vuex-orm/core";
import type { Fields, Query } from "@vuex-orm/core";
import type InheritanceTypes from "@vuex-orm/core/dist/src/model/contracts/InheritanceTypes";
import type { eventWithTime } from "@rrweb/types";
import type { ActionType, modifierKeys } from "@modules/event_processor/types";
import { v4 as uuidv4 } from "uuid";

import type { ActionWithDuration, Speed, Timings } from "./ActionTypes";

// eslint-disable-next-line import/no-cycle
import Video from "./Video";
// eslint-disable-next-line import/no-cycle
import {
  Click,
  Input,
  Keypress,
  Pause,
  Overlay,
  FileSlide,
  TextSlide,
  ImageSlide,
  VideoSlide,
  BrowserActionTypes,
  BrowserAction,
  BrowserPlayback,
  Scroll,
} from "./ActionHierarchy";

// eslint-disable-next-line import/no-cycle
import VoiceOver from "./VoiceOver";
// import FileSlide from "./FileSlide";

type ActionSubclassConstructor =
  | typeof Click
  | typeof BrowserPlayback
  | typeof Scroll
  | typeof Input
  | typeof Keypress
  | typeof Pause
  | typeof Overlay
  | typeof FileSlide
  | typeof TextSlide
  | typeof ImageSlide
  | typeof VideoSlide
  | typeof BrowserAction;

export type ActionModelType =
  | "Action"
  | "Click"
  | "BrowserPlayback"
  | "Scroll"
  | "Input"
  | "Keypress"
  | "Pause"
  | "Overlay"
  | "ImageSlide"
  | "TextSlide"
  | "VideoSlide";

export interface ActionJson {
  id: string;
  type: ActionModelType;
  index: number | null;
  visible: boolean;
  visibleInGuide: boolean;
  previewable: boolean;
  animated: boolean;
  videoId: number;
  video?: Video;
  startsVoiceOver?: VoiceOver;
  voiceOverId: string | null;
  voiceOver?: VoiceOver;
  customDescription: string;
}

export interface BrowserActionJson extends ActionJson {
  events: eventWithTime[];
  elementOuterHtml?: string;
  elementVisible: boolean;
  text?: string;
  key?: string;
  modifierKeys?: modifierKeys[];
  startsAt: number | null;
  endsAt: number | null;
  captureMetaPartition: string;
  captureMetaHref: string | null;
  captureMetaTargetId: number;
}

export type RawBrowserActionJson = Omit<
  BrowserActionJson,
  | "videoId"
  | "screenshots"
  | "voiceOverId"
  | "index"
  | "visible"
  | "visibleInGuide"
  | "previewable"
  | "animated"
  | "customDescription"
>;

export type RawBrowserActionWithVisibleFlag = RawBrowserActionJson &
  (
    | {
        hideIfNew: boolean;
      }
    | {
        visible?: boolean;
      }
  );

export class Action extends Model implements ActionJson {
  // public ["constructor"]: typeof Action;

  // This is the name used as module name of the Vuex Store.
  public static entity = "actions";

  public static types(): InheritanceTypes {
    return {
      Click,
      Input,
      Keypress,
      BrowserPlayback,
      Scroll,
      Pause,
      Overlay,
      ImageSlide,
      TextSlide,
      VideoSlide,
    };
  }

  public static new(): Promise<Action> {
    return super.new() as Promise<Action>;
  }

  // List of all fields (schema) of the post model. `this.attr` is used
  // for the generic field type. The argument is the default value.
  public static fields(): Fields {
    return {
      id: this.uid(() => uuidv4()),

      // Warning: Do not provide the type with a default value.
      // if you do then calling ActionSubClass.new() will never have it's `type` set
      // and all actions will have `type: "Action"`
      // (substitute "Action" for what - ever you provide as a default value)
      //
      // We are disabling typescript for this line as it is not correct
      // Please remove when upgrading to VuexORM 1.0
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      type: this.string(),
      // /End Warning

      index: this.number(null).nullable(),
      visible: this.boolean(true),
      visibleInGuide: this.boolean(true),
      customDescription: this.string(""),
      // TODO: rename me to `playable`
      previewable: this.boolean(true),
      // duration: this.number(), // DEPRECATED
      animated: this.boolean(true),
      videoId: this.number(0),
      video: this.belongsTo(Video, "videoId"),
      startsVoiceOver: this.hasOne(VoiceOver, "startAtId"),
      endsVoiceOver: this.hasOne(VoiceOver, "endAtId"),
      voiceOverId: this.attr(null).nullable(),
      voiceOver: this.belongsTo(VoiceOver, "voiceOverId"),
      // overlay: this.hasOne(Overlay, "overlayId"),
      speed: this.string("medium"),
      // in BrowserAction, but if we don’t define the association here in the STI root class, then
      // Vuex ORM will not notice it when the snapshot is present in API responses.
      // name: this.string(""), // DEPRECATED -> TODO: remove this attribute
    };
  }

  public declare id: string;

  public declare type: ActionModelType;

  /**
   * @deprecated
   * Only used in Vuex-ORM to guarantee order, being phased out in favor of ordered arrays
   */
  public declare index: number | null;

  /**
   * Indicates if this action should be visible in the player.
   */
  public declare visible: boolean;

  public declare visibleInGuide: boolean;

  public declare customDescription: string;

  /**
   * Indicates if this action can be played in the player.
   * Will be false if its events haven't gone through the asset cacher yet.
   */
  public declare previewable: boolean;

  public declare videoId: number;

  public declare video: Video;

  public declare startsVoiceOver: VoiceOver;

  public declare endsVoiceOver: VoiceOver;

  public declare voiceOverId: string | null;

  public declare voiceOver: VoiceOver | undefined;

  public declare speed: Speed;

  public declare animated: boolean;

  public static get humanName(): string {
    return (this.name || "").charAt(0).toUpperCase() + (this.name || "").slice(1);
  }

  public get label(): string {
    return (this.constructor as typeof Action | ActionSubclassConstructor).humanName;
  }

  public get description(): string {
    return this.label;
  }

  public get descriptionForGuide(): string {
    return this.customDescription || this.description;
  }

  // eslint-disable-next-line class-methods-use-this
  public get defaultTimings(): Omit<Timings, "total"> {
    return {
      before: 0.0,
      main: 0.0,
      after: 0.0,
    };
  }

  public static async transformRawActionToData(
    rawAction: ActionType,
  ): Promise<RawBrowserActionJson> {
    const { element, ...action } = rawAction;
    const transformed: RawBrowserActionJson = {
      ...action,
      type: rawAction.name,
      startsAt: first(rawAction.events)?.timestamp || null,
      endsAt: last(rawAction.events)?.timestamp || null,

      captureMetaHref: rawAction.href,
      captureMetaTargetId: rawAction.elementId,
      captureMetaPartition: "persist:user-recording", // TODO: make this configurable
    };
    if (element) transformed.elementOuterHtml = element.outerHTML;

    return transformed;
  }

  public static queryForVideo(videoId: number): Query<Action> {
    return this.query().where("videoId", videoId).orderBy("index");
  }

  public get absoluteTimings(): Timings {
    const timings = this.defaultTimings;
    const { duration } = this as ActionWithDuration;
    if (duration && duration !== 0.0) {
      timings.main = duration;
    } else if (this instanceof Click) {
      timings.main = Math.max(timings.main, ((this.endsAt || 0) - (this.startsAt || 0)) / 1000);
    }
    return {
      ...timings,
      total: sum(Object.values(timings)),
    };
  }

  public get timings(): Timings {
    if (!this.visible) {
      // Hidden actions should not have any duration.
      return {
        before: 0.0,
        main: 0.0,
        after: 0.0,
        total: 0.0,
      };
    }
    return this.absoluteTimings;
  }

  /**
   * @deprecated
   */
  public get isFirst(): boolean {
    return this.index === 0;
  }

  /**
   * @deprecated
   */
  public get previousAction(): Action | null {
    if (this.index === null) return null;

    return Action.query()
      .where("videoId", this.videoId)
      .where("index", this.index - 1)
      .last();
  }

  /**
   * @deprecated
   */
  public get nextAction(): Action | null {
    // return Video.query().with('actions').find(this.videoId).actions[this.index + 1];
    if (this.index === null) return null;

    return Action.query()
      .where("videoId", this.videoId)
      .where("index", this.index + 1)
      .first();
  }

  /**
   * Using all actions NOT JUST VISIBLE ACTIONS we can find the next action.
   * @param action - current action
   * @param actions - all actions, INCLUDING HIDDEN ACTIONS, otherwise this won't work
   */
  public static nextAction(action: Action, actions: Action[]): Action | null {
    const currentPosition = actions.indexOf(action);
    return actions[currentPosition + 1] || null;
  }

  /**
   * @deprecated
   */
  public get nextBrowserAction(): BrowserAction | null {
    // return Video.query().with('actions').find(this.videoId).actions[this.index + 1];
    return this.nextActionsQuery
      .where("type", (value) => BrowserActionTypes().includes(value))
      .first() as BrowserAction;
  }

  /**
   * @deprecated
   */
  public get previousBrowserAction(): BrowserAction | null {
    return this.previousActionsQuery
      .where("type", (value) => BrowserActionTypes().includes(value))
      .first() as BrowserAction;
  }

  /**
   * @deprecated
   */
  public get isLast(): boolean {
    return !this.nextAction;
  }

  /**
   * Using all actions NOT JUST VISIBLE ACTIONS we can figure out if this is the last action.
   * @param action - action to test
   * @param actions - all actions, INCLUDING HIDDEN ACTIONS, otherwise this won't work
   */
  public static isLast(action: Action, actions: Action[]): boolean {
    return !this.nextAction(action, actions);
  }

  /**
   * @deprecated
   */
  public get previousActionsQuery(): Query<Action> {
    if (this.index === null)
      // eslint-disable-next-line no-console
      console.error(
        "previousActionsQuery: Action is being queried while having an index of null",
        this,
      );
    return Action.query()
      .where("videoId", this.videoId)
      .where("index", (index) => index < (this.index as number))
      .orderBy("index");
  }

  /**
   * @deprecated
   */
  public get nextActionsQuery(): Query<Action> {
    if (this.index === null)
      // eslint-disable-next-line no-console
      console.error(
        "nextActionsQuery: Action is being queried while having an index of null",
        this,
      );
    return Action.query()
      .where("videoId", this.videoId)
      .where("index", (index) => index > (this.index as number))
      .orderBy("index");
  }

  /**
   * @deprecated
   */
  public get previousVisiblePlayableBrowserAction(): BrowserAction | null {
    return this.previousActionsQuery
      .where("isPlayable", true)
      .where("visible", true)
      .where("type", (value) => BrowserActionTypes().includes(value))
      .last() as BrowserAction;
  }

  /**
   * @deprecated
   */
  public get nextVisiblePlayableAction(): Action | null {
    return this.nextActionsQuery.where("isPlayable", true).where("visible", true).first();
  }

  /**
   * @deprecated
   */
  public get nextVisiblePlayableBrowserAction(): BrowserAction | null {
    return this.nextActionsQuery
      .where("isPlayable", true)
      .where("visible", true)
      .where("type", (value) => BrowserActionTypes().includes(value))
      .first() as BrowserAction;
  }

  /**
   * @deprecated
   */
  public get previousVisibleAction(): Action | null {
    return this.previousActionsQuery.where("visible", true).last();
  }

  /**
   * @deprecated
   */
  public get nextVisibleAction(): Action | null {
    return this.nextActionsQuery.where("visible", true).first();
  }

  public static firstVisibleAction(actions: Action[]): Action | undefined {
    return actions.find((action) => action.visible) || actions[0];
  }

  public readonly isBrowserAction: boolean = false;

  // eslint-disable-next-line class-methods-use-this
  public get isPlayable(): boolean {
    return true;
  }

  public get initialPlayableTimestamp(): number | null {
    // This method returns the timestamp that the rrweb-player should display by default.
    return this.nextVisiblePlayableBrowserAction && this.nextVisiblePlayableBrowserAction.startsAt;
  }

  /**
   * @deprecated
   */
  public static visibleOverlay(currentAction: Action, actions: Action[]): Overlay | undefined {
    if (!currentAction) return undefined;
    if (currentAction instanceof Overlay) return currentAction;

    const overlays = actions.filter(
      (action) => action.visible && action instanceof Overlay,
    ) as Overlay[];

    const lastOverlay = overlays
      .slice()
      .reverse()
      .find((overlay) => {
        if (overlay.index === null) return false;
        return overlay.index < (currentAction?.index || 0);
      });
    if (!lastOverlay) return undefined;

    const nextOverlay = overlays.find((overlay) => {
      if (overlay.index === null) return false;
      return overlay.index > (currentAction?.index || 0);
    });

    let elapsed = 0.0;
    const actionsWithOverlay = actions
      .slice(lastOverlay.index!, nextOverlay?.index || undefined)
      .filter((action) => {
        if (elapsed > lastOverlay.estimatedReadingTime) return false;
        if (action.visible) {
          elapsed += action.timings.total || 0;
        }
        return elapsed > lastOverlay.estimatedReadingTime;
      });

    if (actionsWithOverlay.some((action) => action.id === currentAction.id)) {
      return lastOverlay;
    }
    return undefined;
  }

  public static getScreenshotURL(
    action: {
      fullScreenshotFileKey: string | null;
      closeupScreenshotFileKey: string | null;
    },
    baseFileUrl: string,
    preference: "closeup" | "full",
    useFallback = false,
  ): string | undefined {
    const types = ["closeup", "full"] as const;
    const orderedTypes = useFallback
      ? [preference, ...types.filter((type) => type !== preference)]
      : [preference];
    const availableType: (typeof types)[number] | undefined = orderedTypes.find(
      (type) => action[`${type}ScreenshotFileKey`],
    );
    const key = action[`${availableType}ScreenshotFileKey`];
    return availableType && key ? Video.fileUrl(baseFileUrl, key) : undefined;
  }

  public static lastActionWithScreenshot(actions: Action[]): BrowserAction | undefined {
    return actions
      .slice() // new array
      .reverse()
      .find((action) => {
        if (action instanceof BrowserAction) {
          return action.fullScreenshotFileKey || action.closeupScreenshotFileKey;
        }
        return undefined;
      }) as BrowserAction | undefined;
  }

  // filterVisibleActions would be a better name for this method
  public static getVisibleActions(actions: Action[]): Action[] {
    return actions.filter((action) => action.visible);
  }
}

export default Action;
